<script setup>
import SourceGA from "@/components/reporting/partials/SourceGA.vue";
import {computed} from "vue";
import TrendTag from "@/components/global-themed/TrendTag.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import StatsEmpty from "@/components/reporting/partials/StatsEmpty.vue";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";

const props = defineProps({
  value: {
    type: Number,
    required: false,
    default: undefined,
  },
  compare: {
    type: Number,
    required: false,
    default: undefined,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  formatter: {
    type: Function,
    required: false,
    default: ((value) => value?.toLocaleString()),
  },
  accountMissing: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  }
})

const canBePercent = computed(() => props.value > 0 && props.compare);

const diff = computed(() => {
  if (props.value === undefined || props.compare === undefined) return undefined;

  const value = props.value || 0;
  const compare = props.compare || 0;

  if (!canBePercent.value) return value - compare;

  return (((value - compare) / compare) * 100);
});

const diffSymbol = computed(() => {
  if (diff.value === undefined) return undefined;

  return (diff.value > 0) ? '+' : '';
});

const suffix = computed(() => canBePercent.value > 0 ? '%' : '');

</script>
<template>
  <div class="tw-rounded-lg tw-p-6 tw-flex tw-flex-col tw-gap-4 tw-border">
    <div class="tw-flex tw-gap-1 tw-text-grey-dark tw-uppercase tw-items-center">
      <slot name="title" />
    </div>
    <LoadingBlock v-if="loading" />
    <ConnectAccount
      v-else-if="accountMissing"
      inline
    />
    <ErrorBlock
      v-else-if="error"
      inline
    />
    <StatsEmpty v-else-if="!loading && value === undefined" />
    <div
      v-else-if="compare === undefined"
      class="tw-text-blue tw-text-4xl tw-font-medium"
    >
      {{ value === undefined ? '-' : formatter(value) }}
    </div>
    <div v-else>
      <div class="tw-flex tw-items-center tw-gap-2 tw-text-grey-dark tw-mb-2">
        <span class="tw-text-4xl tw-font-medium">{{ `${diffSymbol}${diff?.toFixed(0)}${suffix}` }}</span>
        <TrendTag
          v-show="diff !== 0"
          :trend="diff"
        />
      </div>
      <div class="tw-flex tw-items-center tw-gap-4">
        <div
          v-tooltip="{
            content: 'Current period',
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          class="tw-flex tw-items-center tw-gap-1"
        >
          <svg-vue
            icon="icons/calendar"
            class="tw-h-6 tw-w-6"
          />
          <span class="tw-text-blue tw-text-lg tw-font-medium tw-antianalised ">
            {{ formatter(value) }}
          </span>
        </div>

        <div
          v-tooltip="{
            content: 'Previous period',
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          class="tw-flex tw-items-center tw-gap-1"
        >
          <svg-vue
            icon="icons/calendar"
            class="tw-h-6 tw-w-6"
          />
          <span class="tw-text-purple tw-text-lg tw-font-medium tw-antianalised">
            {{ formatter(compare) }}
          </span>
        </div>
      </div>
    </div>
    <div class="tw-mt-4">
      <slot name="source">
        <SourceGA />
      </slot>
    </div>
  </div>
</template>